import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import store from '../store';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';
import { translate } from '../i18n/translate';

/*
  Idle logout, paired with the backend's session lifetime.

  The backend now runs a 12-hour SLIDING idle session, so a 30-minute client
  timeout was the shorter of the two and the one users actually hit - logged
  out mid-matter while the server still held a perfectly good session. These
  two must move together; if the backend lifetime changes, change this.

  The warning is a fixed five minutes ahead of the logout, not a fraction of
  it, so `Your session will expire in 5 minutes…` stays literally true however
  long the timeout becomes.
*/
const IDLE_TIMEOUT_MS = 12 * 60 * 60 * 1000;
const IDLE_WARNING_LEAD_MS = 5 * 60 * 1000;
const IDLE_WARNING_MS = IDLE_TIMEOUT_MS - IDLE_WARNING_LEAD_MS;

export const useSessionManagement = () => {
  const { isAuthenticated, logout } = useAuth0();
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetIdleTimeout = () => {
    if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);

    if (!isAuthenticated) {
      return;
    }

    warningTimeoutRef.current = setTimeout(() => {
      store.dispatch(showFeedbackModal({
        showModal: true,
        message: translate('Your session will expire in 5 minutes due to inactivity.'),
        modalType: 'warning',
        duration: 5000
      }));
    }, IDLE_WARNING_MS);

    activityTimeoutRef.current = setTimeout(() => {
      localStorage.clear();
      sessionStorage.clear();

      store.dispatch(showFeedbackModal({
        showModal: true,
        message: translate('Your session has expired. Please log in again.'),
        modalType: 'warning',
        duration: 3000
      }));

      logout({ logoutParams: { returnTo: window.location.origin } });
    }, IDLE_TIMEOUT_MS);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      return;
    }

    resetIdleTimeout();

    const handleUserActivity = () => {
      resetIdleTimeout();
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => document.addEventListener(event, handleUserActivity));

    return () => {
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      events.forEach(event => document.removeEventListener(event, handleUserActivity));
    };
  }, [isAuthenticated, logout]);
};

export default useSessionManagement;
